import { defaultFetch, defaultFetchWithMeta } from './baseApi';

type StatementType = {
	id: number;
	sequence: number;
	name: string;
	date: string | null;
	amount: number;
	state: string;
	sent: boolean;
	approved: boolean;
	note: string | null;
	monthsReportingEndDate: number;
	monthsReportingStartDate: number;
	monthsApprovedDate: number;
	monthsSentDate: number;

	project: {
		id: number;
		name: string;
		description: string;
		manager: string | null;
		cup: string;
		network: boolean;
		note: string;
		amount: string | null;
		cuts: string | null;
		applicant: string | null;
		hidden: boolean;
		sco: boolean;
		company: {
			id: number;
			name: string;
			vat: string;
			address: string | null;
			pec: string | null;
			attorney: string | null;
			code: string;
			oecompanysize: {
				code: string;
				name: string;
				description: string;
			};
			sizeCode: string;
			legalRepresentative: string | null;
			powerOfAttorney: number;
			payrollMgmtModule: boolean;
			aiEnabled: boolean;
			createdBy: number;
			updatedBy: number;
			hasModulePartnership: boolean;
		};
		notice: {
			id: number;
			name: string;
			objectives: string | null;
			disbursement: string | null;
			timeframe: string | null;
			compounding: string | null;
			dnsh: number;
			call: string;
			fund: {
				id: number;
				code: string;
				name: string;
				description: string;
				createdBy: string | null;
				updatedBy: string | null;
			};
			instrument: {
				id: number;
				name: string;
				managingAuthority: string;
				createdBy: number;
				updatedBy: number;
			};
			institution: {
				id: number;
				code: string;
				name: string;
				managingSystem: string;
				createdBy: number;
				updatedBy: number;
			};
			fundId: number;
			instrumentId: number;
			questionsetId: number;
			publishedAt: string;
			expiresAt: string;
			institutionId: number;
			institutionCode: string;
			admissibleCosts: string | null;
			advancePaymentPercentage: string | null;
			changeManagement: string | null;
			managingSystem: string | null;
			digitalTagging: number;
			environmentalTagging: number;
			genderEquality: number;
			reportableEmployeeDays: string | null;
			createdBy: number;
			updatedBy: string | null;
			questionSet: {
				id: number;
				name: string;
				description: string;
				used: boolean;
				default: boolean;
				legacyType: string;
				createdBy: string | null;
				updatedBy: string | null;
			};
		};
		oeManager: string | null;
		location: string | null;
		companyId: number;
		noticeId: number;
		streamConfigurationType: string;
		durationInMonths: number;
		oeManagerId: number | null;
		forecastStartDate: string;
		forecastEndDate: string;
		startDate: string | null;
		endDate: string | null;
		networkNote: string;
		aiEnabled: boolean;
		locationCode: string | null;
		finalAmount: string | null;
		assignedAmount: string | null;
		totalAdmissibleAmount: string | null;
		totalApprovedAmount: string | null;
		indirectCostsPerc: number;
		payrollMgmtModule: boolean;
		reportableEmployeeDays: string | null;
		vatRate: string | null;
		localProjectCode: string | null;
		projectCodeStamp: string | null;
		applicantEmail: string | null;
		dnshMitigationActivityNumber: string | null;
		dnshAdaptationActivityNumber: string | null;
		dnshAdaptationActivity: string | null;
		dnshAdaptationSector: string | null;
		dnshMitigationActivity: string | null;
		dnshMitigationSector: string | null;
		dnshClimateAdaptation: boolean;
		dnshClimateMitigation: boolean;
		dnshElement: string | null;
		createdBy: number;
		updatedBy: number;
		hasPartnership: boolean;
	};
	oestatementtype: {
		code: string;
		name: string;
		description: string;
	};
	projectId: number;
	statementTypeCode: string;
	reportingStartDate: string | null;
	reportingEndDate: string | null;
	cashedIn: boolean;
	cashedInDate: string | null;
	requestedAmount: number;
	approvedAmount: number;
	indirectCost: number;
	indirectInvoiceCost: number;
	helpPercentageIntensity: number;
	contextAndObjectives: string | null;
	projectPolicyRelevance: string | null;
	expectedOutcomes: string | null;
	workPerformedAndResults: string | null;
	approvedDate: string | null;
	sentDate: string | null;
	createdBy: number;
	updatedBy: number;
};

export const getStatementsByProjectId = async (projectId: string, state?: 'DRAFT' | 'VALIDATED') => {
	let url = `/v0/projects/${projectId}/statements?statement_type_code=CR`;
	if (state) url += `&state=${state}`;

	return defaultFetch<StatementType[]>(url);
};

export const createStatementByProjectId = async (
	projectId: string,
	params: {
		statementTypeCode: 'CR';
		note: string;
	},
) => {
	const url = `/v0/projects/${projectId}/statements`;
	return defaultFetch<StatementType>(url, {
		method: 'POST',
		body: JSON.stringify(params),
	});
};

export const updateStatementByProjectIdAndStatementId = async (
	projectId: string,
	statementId: number,
	params: {
		statementTypeCode: 'CR';
		contextAndObjectives: string;
		workPerformedAndResults: string;
		expectedOutcomes: string;
		projectPolicyRelevance: string;
	},
) => {
	const url = `/v0/projects/${projectId}/statements/${statementId}`;
	return defaultFetch<StatementType>(url, {
		method: 'PUT',
		body: JSON.stringify(params),
	});
};

export const updateStatementStatusByProjectIdAndStatementId = async (
	projectId: string,
	statementId: number,
	params: {
		statementTypeCode: 'CR';
		sent: boolean;
		sentDate: string;
		approved: boolean;
		approvedDate: string;
	},
) => {
	const url = `/v0/projects/${projectId}/statements/${statementId}`;
	return defaultFetch<StatementType>(url, {
		method: 'PUT',
		body: JSON.stringify(params),
	});
};

export const validateStatementByProjectIdAndStatementId = async (projectId: string, statementId: number) => {
	const url = `/v0/projects/${projectId}/statements/${statementId}`;
	return defaultFetch<StatementType>(url, {
		method: 'PUT',
		body: JSON.stringify({ state: 'VALIDATED' }),
	});
};

export const deleteStatementByProjectIdAndStatementId = async (projectId: string, statementId: number) => {
	const url = `/v0/projects/${projectId}/statements/${statementId}`;
	return defaultFetch(url, {
		method: 'DELETE',
	});
};

export const sendReportingDocuments = async (projectId: string, statementId: number) => {
	const url = `/v0/projects/${projectId}/statements/${statementId}/export/documents`;
	return defaultFetch<{
		success: boolean;
		message: string;
		data: string;
	}>(url, {
		method: 'GET',
	});
};

// get deliverable and milestones by projectId and statementId
type BaseDeliverableOrMilestoneType = {
	id: number;
	title: string;
	description: string | null;
	owner: string;
	forecastDate: string;
	effectiveDate: string | null;
	note: string | null;
	checked: number;
	responsibleContact: {
		companies: {
			name: string;
			id: number;
		}[];
	};
};

type DeliverableType = BaseDeliverableOrMilestoneType & {
	type: 'D';
};

type MilestoneType = BaseDeliverableOrMilestoneType & {
	type: 'M';
};

export type DeliverableOrMilestoneType = DeliverableType | MilestoneType;

type MetaType = {
	total: number;
	currentPage: number;
	perPage: number;
};

const createDeliverablesAndMilestonesUrl = (url: string, searchParams: { currentPage?: number; perPage?: number; searchTerm?: string }) => {
	const params: string[] = [];

	if (searchParams.currentPage) params.push(`page=${searchParams.currentPage}`);
	if (searchParams.perPage) params.push(`perPage=${searchParams.perPage}`);
	if (searchParams.searchTerm) params.push(`term=${searchParams.searchTerm}`);

	if (params.length > 0) {
		url += `?${params.join('&')}`;
	}
	return url;
};
export const getDeliverablesAndMilestonesByProjectIdAndStatementId = async (
	projectId: string,
	statementId: number,
	searchParams: {
		currentPage?: number;
		perPage?: number;
		searchTerm?: string;
	},
) => {
	const url = createDeliverablesAndMilestonesUrl(`/v0/projects/${projectId}/statements/${statementId}/dms`, searchParams);

	return defaultFetchWithMeta<DeliverableOrMilestoneType[], MetaType>(url);
};

// pick/unpick deliverable and milestone by projectId and statementId
export const pickAllDeliverablesAndMilestonesByProjectIdAndStatementId = async (
	projectId: string,
	statementId: number,
	deliverableIds: number[],
	milestoneIds: number[],
) => {
	const url = `/v0/projects/${projectId}/statements/${statementId}/dms/pickall`;
	const body = JSON.stringify({
		deliverableIds,
		milestoneIds,
	});
	return defaultFetch<DeliverableOrMilestoneType>(url, {
		method: 'POST',
		body,
	});
};
export const unpickAllDeliverablesAndMilestonesByProjectIdAndStatementId = async (
	projectId: string,
	statementId: number,
	deliverableIds: number[],
	milestoneIds: number[],
) => {
	const url = `/v0/projects/${projectId}/statements/${statementId}/dms/unpickall`;
	const body = JSON.stringify({
		deliverableIds,
		milestoneIds,
	});
	return defaultFetch<DeliverableOrMilestoneType>(url, {
		method: 'POST',
		body,
	});
};
